/**
 * Gera XLSX do lado do cliente via SheetJS.
 *
 * Cada aba é descrita por `{ name, rows, columns?, freezeHeader? }`.
 *  - `rows`: array de objetos (chaves viram colunas se `columns` não
 *    for passado).
 *  - `columns`: ordem + cabeçalho amigável + formato por coluna.
 *    `format`:
 *      - `'money'` aplica `R$ #,##0.00`
 *      - `'date'` aplica `dd/mm/yyyy`
 *      - `'integer'` aplica `0`
 *      - undefined deixa o tipo nativo da célula.
 *  - `freezeHeader`: trava a primeira linha ao rolar.
 *
 * Sem deps além do `xlsx`. Tudo offline.
 */
import * as XLSX from 'xlsx';

export interface XlsxColumn<T> {
  key: keyof T & string;
  header: string;
  format?: 'money' | 'date' | 'integer';
  width?: number;
}

export interface XlsxSheet<T> {
  name: string;
  rows: T[];
  columns?: XlsxColumn<T>[];
  freezeHeader?: boolean;
}

const FORMAT_FMT: Record<NonNullable<XlsxColumn<unknown>['format']>, string> = {
  money: '"R$"#,##0.00',
  date: 'dd/mm/yyyy',
  integer: '0',
};

function buildSheet<T extends Record<string, unknown>>(sheet: XlsxSheet<T>): XLSX.WorkSheet {
  const cols =
    sheet.columns ??
    (sheet.rows[0]
      ? (Object.keys(sheet.rows[0]).map((k) => ({ key: k, header: k })) as XlsxColumn<T>[])
      : []);

  const aoa: unknown[][] = [cols.map((c) => c.header)];
  for (const row of sheet.rows) {
    aoa.push(cols.map((c) => row[c.key]));
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Aplica formato por coluna (linha 2+ é dado; linha 1 é header).
  if (sheet.rows.length > 0) {
    for (let colIdx = 0; colIdx < cols.length; colIdx++) {
      const fmt = cols[colIdx]?.format;
      if (!fmt) continue;
      const z = FORMAT_FMT[fmt];
      for (let rowIdx = 1; rowIdx <= sheet.rows.length; rowIdx++) {
        const addr = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
        const cell = ws[addr] as XLSX.CellObject | undefined;
        if (cell) cell.z = z;
      }
    }
  }

  // Larguras
  ws['!cols'] = cols.map((c) => ({ wch: c.width ?? Math.max(c.header.length + 2, 10) }));

  // Freeze header
  if (sheet.freezeHeader !== false) {
    ws['!freeze'] = { xSplit: '0', ySplit: '1', topLeftCell: 'A2', activePane: 'bottomLeft' };
  }

  return ws;
}

export function downloadAsXlsx(
  filename: string,
  sheets: XlsxSheet<Record<string, unknown>>[],
): void {
  const wb = XLSX.utils.book_new();
  for (const sheet of sheets) {
    const ws = buildSheet(sheet);
    XLSX.utils.book_append_sheet(wb, ws, sheet.name.slice(0, 31)); // limite XLSX
  }
  const name = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, name);
}
